/**
 * Gallery Variant Optimizer
 * Simple loading strategy:
 * - Eager loads ONLY the featured media of selected variant
 * - Lazy loads ALL other images as they enter viewport
 * - Actually prevents loading by removing src AND srcset until needed
 */

(function() {
  'use strict';
  
  
  let currentVariantId = null;
  let imageObserver = null;
  
  // Wait for DOM ready and other scripts to load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(init, 500);
    });
  } else {
    setTimeout(init, 500);
  }
  
  function init() {
    // Check if we're on a product page
    const productImages = document.querySelectorAll('.product-media__image, .gallery-image, img[data-media-id]');
    
    if (productImages.length === 0) {
      return;
    }
    
    
    // Process images immediately to prevent duplicate loads
    productImages.forEach((img, index) => {
      // Ensure gallery-image class
      if (!img.classList.contains('gallery-image')) {
        img.classList.add('gallery-image');
      }
      
      // Store original values if not already stored
      if (!img.dataset.originalSrc && img.src && !img.src.includes('data:image/svg')) {
        img.dataset.originalSrc = img.src;
      }
      if (!img.dataset.originalSrcset && img.srcset) {
        img.dataset.originalSrcset = img.srcset;
      }
      
      // Mark optimization as processed
      if (img.dataset.needsOptimization === 'true') {
        img.dataset.needsOptimization = 'false';
        
        // For non-featured images, remove src to prevent loading
        if (index > 0) {
          img.src = 'data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 1 1%27%3E%3C/svg%3E';
          img.removeAttribute('srcset');
        }
      }
    });
    
    // Setup Intersection Observer for lazy loading
    setupIntersectionObserver();
    
    // Get initial variant
    currentVariantId = getCurrentVariantId();
    
    // Apply initial loading strategy
    if (currentVariantId) {
      applyLoadingStrategy(currentVariantId);
    }
    
    // Setup listeners
    setupVariantChangeListener();
  }
  
  function setupIntersectionObserver() {
    if ('IntersectionObserver' in window) {
      imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            
            // Load the image if it doesn't have real src set
            if (!img.src || img.src.includes('data:image/svg')) {
              if (img.dataset.originalSrc) {
                img.src = img.dataset.originalSrc;
                
                // Restore srcset if it exists
                if (img.dataset.originalSrcset) {
                  img.srcset = img.dataset.originalSrcset;
                }
                
                // Stop observing this image once loaded
                imageObserver.unobserve(img);
              }
            }
          }
        });
      }, {
        rootMargin: '50px',
        threshold: 0.01
      });
    }
  }
  
  function getCurrentVariantId() {
    // Get current variant from various sources
    const variantInput = document.querySelector('input[name="id"]:checked, select[name="id"], input[name="id"][type="hidden"]');
    if (variantInput && variantInput.value) {
      return variantInput.value;
    }
    
    // Check URL
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('variant');
  }
  
  function applyLoadingStrategy(variantId) {
    const variantData = getVariantData(variantId);
    if (!variantData) {
      return;
    }
    
    const featuredMediaId = variantData.featured_media?.id;
    
    // Process all gallery images
    const galleryImages = document.querySelectorAll('.gallery-image');
    let eagerCount = 0;
    let lazyCount = 0;
    
    galleryImages.forEach((img, index) => {
      const mediaId = img.dataset.mediaId;
      
      // Clear any previous observer
      if (imageObserver) {
        imageObserver.unobserve(img);
      }
      
      if (mediaId == featuredMediaId) {
        // This is the featured image - load it immediately
        eagerCount++;
        img.loading = 'eager';
        img.setAttribute('fetchpriority', 'high');
        
        // Restore src and srcset immediately
        if (img.dataset.originalSrc) {
          img.src = img.dataset.originalSrc;
        }
        if (img.dataset.originalSrcset) {
          img.srcset = img.dataset.originalSrcset;
        }
        
      } else {
        // All other images - lazy load
        lazyCount++;
        img.loading = 'lazy';
        img.setAttribute('fetchpriority', 'auto');
        
        // ALWAYS remove src and srcset to prevent loading
        img.src = 'data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 1 1%27%3E%3C/svg%3E';
        img.removeAttribute('srcset');
        
        // Add to intersection observer
        if (imageObserver) {
          imageObserver.observe(img);
        }
        
      }
    });
    
  }
  
  function setupVariantChangeListener() {
    // Listen for variant changes
    document.addEventListener('variant:change', handleVariantChange);
    
    // Listen for form changes
    const variantInputs = document.querySelectorAll('input[name="id"], select[name="id"]');
    variantInputs.forEach(input => {
      input.addEventListener('change', function() {
        const newVariantId = this.value;
        if (newVariantId !== currentVariantId) {
          currentVariantId = newVariantId;
          applyLoadingStrategy(newVariantId);
        }
      });
    });
    
    // Watch for clicks on variant selectors (color swatches)
    document.addEventListener('click', function(e) {
      const variantSelector = e.target.closest('[data-variant-id], .variant-picker__option, .product-form__input input[type="radio"]');
      if (variantSelector) {
        setTimeout(() => {
          const newVariantId = getCurrentVariantId();
          if (newVariantId && newVariantId !== currentVariantId) {
            currentVariantId = newVariantId;
            applyLoadingStrategy(newVariantId);
          }
        }, 100);
      }
    });
  }
  
  function handleVariantChange(event) {
    const variantId = event.detail?.variant?.id || event.detail?.variantId;
    if (variantId && variantId !== currentVariantId) {
      currentVariantId = variantId;
      applyLoadingStrategy(variantId);
    }
  }
  
  function getVariantData(variantId) {
    const variants = getAllVariants();
    if (variants) {
      return variants.find(v => v.id == variantId);
    }
    return null;
  }
  
  function getAllVariants() {
    // Try to get all variants from various sources
    
    // 1. Check product-data script
    const productDataScript = document.querySelector('script#product-data, script[type="application/json"][id*="product"]');
    if (productDataScript) {
      try {
        const data = JSON.parse(productDataScript.textContent);
        if (data.product && data.product.variants) {
          return data.product.variants;
        }
        if (data.variants) {
          return data.variants;
        }
      } catch (e) {
      }
    }
    
    // 2. Check for fitcheck variant data
    const fitcheckData = document.querySelector('#fitcheck-variant-data');
    if (fitcheckData) {
      try {
        const data = JSON.parse(fitcheckData.textContent);
        if (data.variants) {
          return data.variants;
        }
      } catch (e) {
      }
    }
    
    // 3. Window.productVariants
    if (window.productVariants) {
      return window.productVariants;
    }
    
    // 4. Shopify global
    if (window.Shopify && window.Shopify.product) {
      return window.Shopify.product.variants;
    }
    
    return null;
  }
  
})();